import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProvisionRunStatus } from '@frontend/types/provision';

const TERMINAL = new Set(['completed', 'partial', 'failed']);
const DEFAULT_INTERVAL_MS = 2000;

export interface UseProvisionPollOptions {
  token: string | null;
  correlationId: string | null;
  fetchStatus: (token: string, correlationId: string) => Promise<ProvisionRunStatus>;
  enabled?: boolean;
  intervalMs?: number;
  onTerminal?: (status: ProvisionRunStatus) => void;
}

export function useProvisionPoll({
  token,
  correlationId,
  fetchStatus,
  enabled = true,
  intervalMs = DEFAULT_INTERVAL_MS,
  onTerminal,
}: UseProvisionPollOptions) {
  const [status, setStatus] = useState<ProvisionRunStatus | null>(null);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  const pollOnce = useCallback(async () => {
    if (!token || !correlationId) return null;
    const next = await fetchStatus(token, correlationId);
    setStatus(next);
    setError('');
    if (TERMINAL.has(next.runState)) {
      onTerminalRef.current?.(next);
      setPolling(false);
    }
    return next;
  }, [token, correlationId, fetchStatus]);

  useEffect(() => {
    if (!enabled || !token || !correlationId) {
      setPolling(false);
      return undefined;
    }

    let cancelled = false;
    setPolling(true);
    setError('');
    setStatus(null);

    const tick = async () => {
      try {
        await pollOnce();
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load provision status');
          setPolling(false);
        }
      }
    };

    tick();
    const id = window.setInterval(tick, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, token, correlationId, intervalMs, pollOnce]);

  return { status, error, polling, refresh: pollOnce };
}
